import { FC } from 'react'

import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import { Box } from '@mui/material'

import { TermRef } from '../../types'
import { ReactComponent as AdMobIcon } from './assets/admob.svg'
import { ReactComponent as LiftoffIcon } from './assets/liftoff.svg'
import { ReactComponent as YouTubeIcon } from './assets/youtube.svg'

interface NetworkIconProps {
  network: TermRef
  /** Square size in px for the rendered icon. Defaults to 20. */
  size?: number
}

const brandKey = (value: string): string => value.toLowerCase().replace(/[^a-z]/g, '')

const BRAND_ICONS: Record<string, FC> = {
  youtube: YouTubeIcon,
  liftoff: LiftoffIcon,
  admob: AdMobIcon,
}

/**
 * Renders a small square brand icon for an ad network (Liftoff, AdMob,
 * YouTube, …). Unknown networks fall back to a generic globe glyph so the
 * caller never has to guard against missing icons. Used by both the network
 * filter dropdown and various detail strips so the visual language stays
 * consistent across UA Insights.
 */
export const NetworkIcon: FC<NetworkIconProps> = ({ network, size = 20 }) => {
  const BrandIcon = BRAND_ICONS[brandKey(network.code)] ?? BRAND_ICONS[brandKey(network.name)]
  const inner = BrandIcon ? <BrandIcon /> : <PublicRoundedIcon sx={{ color: 'text.secondary', fontSize: size }} />

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': { width: '100%', height: '100%' },
      }}
    >
      {inner}
    </Box>
  )
}
